import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MailCheck, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 px-4">
      <div className="w-full max-w-md space-y-6">
        <Link to="/" className="inline-flex items-center gap-2">
          <img src="/logo.png" alt="Jamoora" className="h-20 w-20 rounded-xl object-contain" />
          <span className="font-display font-bold text-lg">Jamoora</span>
        </Link>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          {sent ? (
            <div className="text-center space-y-4" data-testid="forgot-success">
              <MailCheck className="h-12 w-12 text-emerald-600 mx-auto" />
              <h1 className="font-display text-2xl font-bold">Check your inbox</h1>
              <p className="text-slate-600 text-sm">
                If an account exists for <span className="font-medium">{email}</span>, we sent a password reset link.
                It expires in 1 hour.
              </p>
              <Button asChild className="w-full bg-blue-600 hover:bg-blue-700" data-testid="forgot-back-login">
                <Link to="/login">Back to sign in</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <h1 className="font-display text-2xl font-bold">Forgot password</h1>
                <p className="text-slate-600 text-sm mt-1">
                  Enter your work email and we&apos;ll send a reset link if an account exists.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="forgot-email" className="text-xs font-medium uppercase tracking-wider">
                  Email
                </Label>
                <Input
                  id="forgot-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email-input"
                  autoComplete="email"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="forgot-submit"
              >
                {loading ? "Sending…" : "Send reset link"}
              </Button>
              <Link
                to="/login"
                className="flex items-center justify-center gap-1 text-sm text-slate-500 hover:text-slate-900"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
